import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../common/Button';
import Nav from '../../IDE/components/Header/Nav';
import AssetList from '../../IDE/components/AssetList';
import AssetSize from '../../IDE/components/AssetSize';
import SketchList from '../../IDE/components/SketchList';
import SharedList from '../../IDE/components/SharedList';
import { RootPage } from '../../../components/RootPage';
import { newProject } from '../../IDE/actions/project';
import { SketchSearchbar } from '../../IDE/components/Searchbar';

import {
  DashboardTabSwitcher,
  TabKey
} from '../components/DashboardTabSwitcher';
import useIsMobile from '../../IDE/hooks/useIsMobile';

const DashboardView = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const params = useParams();
  const location = useLocation();

  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);

  const createNewSketch = () => {
    dispatch(newProject());
  };

  const selectedTabKey = useCallback(() => {
    const path = location.pathname;

    if (/assets/.test(path)) {
      return TabKey.assets;
    }

    if (/shared/.test(path)) {
      return TabKey.shared;
    }

    return TabKey.sketches;
  }, [location.pathname]);

  const ownerName = () => {
    if (params.username) {
      return params.username;
    }

    return user.username;
  };

  // On the bare /sketches,/assets,/shared routes there is no :username param — the dashboard is the
  // logged-in user's own, so resolve to their username (else tab links become "/undefined/..." and the
  // owner-only tabs are hidden).
  const isOwner = () => ownerName() === user.username;

  const renderActionButton = (tabKey) => {
    switch (tabKey) {
      case TabKey.shared:
        return null;
      case TabKey.assets:
        return isOwner() && <AssetSize />;
      case TabKey.sketches:
      default:
        return (
          <>
            {isOwner() && (
              <Button onClick={createNewSketch}>
                {t('DashboardView.NewSketch')}
              </Button>
            )}
            <SketchSearchbar />
          </>
        );
    }
  };

  const renderContent = (tabKey, username, mobile) => {
    switch (tabKey) {
      case TabKey.shared:
        return <SharedList key={username} />;
      case TabKey.assets:
        return <AssetList key={username} mobile={mobile} username={username} />;
      case TabKey.sketches:
      default:
        return (
          <SketchList key={username} mobile={mobile} username={username} />
        );
    }
  };

  const currentTab = selectedTabKey();
  const actions = renderActionButton(currentTab);

  return (
    <RootPage fixedHeight="100%">
      <Nav layout="dashboard" />

      <main className="dashboard-header">
        <div className="dashboard-header__header">
          <h2 className="dashboard-header__header__title">{ownerName()}</h2>
          <div className="dashboard-header__nav">
            <DashboardTabSwitcher
              currentTab={currentTab}
              isOwner={isOwner()}
              username={ownerName()}
            />
            {actions && (
              <div className="dashboard-header__actions">{actions}</div>
            )}
          </div>
        </div>

        <div className="dashboard-content">
          {renderContent(currentTab, ownerName(), isMobile)}
        </div>
      </main>
    </RootPage>
  );
};

export default DashboardView;
